import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getBancos, parseBancos } from "../data/bancos.data.js";
import { getEstados, parseEstados } from "../data/estados.data.js";

/**
 * Hook centralizado para cargar opciones de filtros (bancos y estados)
 * Reutilizable en cualquier componente que necesite estas opciones
 *
 * Optimizado para evitar re-renders innecesarios cuando las queries fallan
 */
export const useFilterOptions = () => {
    // Cargar bancos desde API
    const {
        data: bancosData,
        isLoading: bancosLoading,
        isError: bancosError,
    } = useQuery({
        queryKey: ["bancos"],
        queryFn: ({ signal }) => getBancos(signal),
        select: (data) => parseBancos(data),
        staleTime: 5 * 60 * 1000, // 5 minutos
        retry: false, // No reintentar automáticamente para evitar re-renders continuos
        placeholderData: [], // Mantener array vacío estable durante carga/error
    });

    // Cargar estados desde API
    const {
        data: estadosData,
        isLoading: estadosLoading,
        isError: estadosError,
    } = useQuery({
        queryKey: ["estados"],
        queryFn: ({ signal }) => getEstados(signal),
        select: (data) => parseEstados(data),
        staleTime: 5 * 60 * 1000, // 5 minutos
        retry: false, // No reintentar automáticamente para evitar re-renders continuos
        placeholderData: [], // Mantener array vacío estable durante carga/error
    });

    // Usar valores estables para evitar re-renders
    const bancos = bancosData || [];
    const estados = estadosData || [];

    // Función helper para obtener el label de un banco por su ID
    // Memoizada para mantener referencia estable
    const getBancoLabel = useMemo(() => {
        return (bancoId) => {
            const data = bancosData || [];
            const banco = data.find(b => b.value === bancoId);
            return banco ? banco.label : bancoId;
        };
    }, [bancosData]);

    // Función helper para obtener el label de un estado por su ID
    // Memoizada para mantener referencia estable
    const getEstadoLabel = useMemo(() => {
        return (estadoId) => {
            const data = estadosData || [];
            const estado = data.find(e => e.value === estadoId);
            return estado ? estado.label : estadoId;
        };
    }, [estadosData]);

    return {
        // Datos
        bancos,
        estados,

        // Estados de carga
        bancosLoading,
        estadosLoading,
        isLoading: bancosLoading || estadosLoading,

        // Estados de error
        bancosError,
        estadosError,

        // Helpers
        getBancoLabel,
        getEstadoLabel,
    };
};
